<script setup>
import { onMounted, computed, ref, toRefs } from 'vue';
import { useTimeoutFn } from '@vueuse/core';
import { provideMessageContext } from './provider.js';
import { useTrack } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { LocalStorage } from 'shared/helpers/localStorage';
import { ACCOUNT_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  MESSAGE_TYPES,
  ATTACHMENT_TYPES,
  MESSAGE_VARIANTS,
  SENDER_TYPES,
  ORIENTATION,
  MESSAGE_STATUS,
  CONTENT_TYPES,
} from './constants';

import Avatar from 'next/avatar/Avatar.vue';

import TextBubble from './bubbles/Text/Index.vue';
import ActivityBubble from './bubbles/Activity.vue';
import ImageBubble from './bubbles/Image.vue';
import ViewOnceBubble from './bubbles/ViewOnce.vue';
import FileBubble from './bubbles/File.vue';
import AudioBubble from './bubbles/Audio.vue';
import VideoBubble from './bubbles/Video.vue';
import EmbedBubble from './bubbles/Embed.vue';
import FallbackBubble from './bubbles/Fallback.vue';
import InstagramStoryBubble from './bubbles/InstagramStory.vue';
import EmailBubble from './bubbles/Email/Index.vue';
import UnsupportedBubble from './bubbles/Unsupported.vue';
import ContactBubble from './bubbles/Contact.vue';
import ContactsBubble from './bubbles/Contacts.vue';
import DyteBubble from './bubbles/Dyte.vue';
import LocationBubble from './bubbles/Location.vue';
import CSATBubble from './bubbles/CSAT.vue';
import FormBubble from './bubbles/Form.vue';
import VoiceCallBubble from './bubbles/VoiceCall.vue';
import StickerBubble from './bubbles/Sticker.vue';
import CardsBubble from './bubbles/Cards.vue';
import UnoapiInteractiveBubble from './bubbles/UnoapiInteractive.vue';
import UnoapiCatalogBubble from './bubbles/UnoapiCatalog.vue';

import MessageError from './MessageError.vue';
import UnoapiWarning from './UnoapiWarning.vue';
import ContextMenu from 'dashboard/modules/conversations/components/MessageContextMenu.vue';
import { useInbox } from 'dashboard/composables/useInbox';
import { useBranding } from 'shared/composables/useBranding';

/**
 * @typedef {Object} Attachment
 * @property {number} id - Unique identifier for the attachment
 * @property {number} messageId - ID of the associated message
 * @property {'image'|'audio'|'video'|'file'|'location'|'fallback'|'share'|'story_mention'|'contact'|'ig_reel'} fileType - Type of the attachment (file or image)
 * @property {number} accountId - ID of the associated account
 * @property {string|null} extension - File extension
 * @property {string} dataUrl - URL to access the full attachment data
 * @property {string} thumbUrl - URL to access the thumbnail version
 * @property {number} fileSize - Size of the file in bytes
 * @property {number|null} width - Width of the image if applicable
 * @property {number|null} height - Height of the image if applicable
 */

/**
 * @typedef {Object} Sender
 * @property {Object} additional_attributes - Additional attributes of the sender
 * @property {Object} custom_attributes - Custom attributes of the sender
 * @property {string} email - Email of the sender
 * @property {number} id - ID of the sender
 * @property {string|null} identifier - Identifier of the sender
 * @property {string} name - Name of the sender
 * @property {string|null} phone_number - Phone number of the sender
 * @property {string} thumbnail - Thumbnail URL of the sender
 * @property {string} type - Type of sender
 */

/**
 * @typedef {Object} ContentAttributes
 * @property {string} externalError - an error message to be shown if the message failed to send
 */

/**
 * @typedef {Object} Props
 * @property {('sent'|'delivered'|'read'|'failed'|'progress')} status - The delivery status of the message
 * @property {ContentAttributes} [contentAttributes={}] - Additional attributes of the message content
 * @property {Attachment[]} [attachments=[]] - The attachments associated with the message
 * @property {Sender|null} [sender=null] - The sender information
 * @property {boolean} [private=false] - Whether the message is private
 * @property {number|null} [senderId=null] - The ID of the sender
 * @property {number} createdAt - Timestamp when the message was created
 * @property {number} currentUserId - The ID of the current user
 * @property {number|string} id - The unique identifier for the message
 * @property {number} messageType - The type of message (must be one of MESSAGE_TYPES)
 * @property {string|null} [error=null] - Error message if the message failed to send
 * @property {string|null} [senderType=null] - The type of the sender
 * @property {string} content - The message content
 * @property {boolean} [groupWithNext=false] - Whether the message should be grouped with the next message
 * @property {Object|null} [inReplyTo=null] - The message to which this message is a reply
 * @property {boolean} [isEmailInbox=false] - Whether the message is from an email inbox
 * @property {boolean} [isGroupConversation=false] - Whether the message belongs to a group conversation
 * @property {number} conversationId - The ID of the conversation to which the message belongs
 * @property {number} inboxId - The ID of the inbox to which the message belongs
 */

// eslint-disable-next-line vue/define-macros-order
const props = defineProps({
  id: { type: [Number, String], required: true },
  messageType: {
    type: Number,
    required: true,
    validator: value => Object.values(MESSAGE_TYPES).includes(value),
  },
  status: {
    type: String,
    required: true,
    validator: value => Object.values(MESSAGE_STATUS).includes(value),
  },
  attachments: { type: Array, default: () => [] },
  content: { type: String, default: null },
  contentAttributes: { type: Object, default: () => ({}) },
  contentType: {
    type: String,
    default: 'text',
    validator: value => Object.values(CONTENT_TYPES).includes(value),
  },
  conversationId: { type: Number, required: true },
  createdAt: { type: Number, required: true }, // eslint-disable-line vue/no-unused-properties
  currentUserId: { type: Number, required: true }, // eslint-disable-line vue/no-unused-properties
  groupWithNext: { type: Boolean, default: false },
  inboxId: { type: Number, default: null }, // eslint-disable-line vue/no-unused-properties
  inboxSupportsReplyTo: { type: Object, default: () => ({}) },
  inReplyTo: { type: Object, default: null }, // eslint-disable-line vue/no-unused-properties
  isEmailInbox: { type: Boolean, default: false },
  isGroupConversation: { type: Boolean, default: false },
  private: { type: Boolean, default: false },
  additionalAttributes: { type: Object, default: () => ({}) }, // eslint-disable-line vue/no-unused-properties
  sender: { type: Object, default: null },
  senderId: { type: Number, default: null },
  senderType: { type: String, default: null },
  sourceId: { type: String, default: '' }, // eslint-disable-line vue/no-unused-properties
  deliveryDestination: { type: String, default: 'channel' }, // eslint-disable-line vue/no-unused-properties
  isForwardSelectionActive: { type: Boolean, default: false },
  isForwardSelected: { type: Boolean, default: false },
});

const emit = defineEmits(['retry', 'toggle-forward-selection']);

const contextMenuPosition = ref({});
const showBackgroundHighlight = ref(false);
const showContextMenu = ref(false);
const { t } = useI18n();
const route = useRoute();
const { isAnInternalChannel, isAWhatsAppChannel, isATwilioWhatsAppChannel } =
  useInbox(props.inboxId);
const inboxGetter = useMapGetter('inboxes/getInbox');
const inbox = computed(() => inboxGetter.value(props.inboxId) || {});
const isUnoapiInbox = computed(
  () =>
    inbox.value.channel_type === 'Channel::Whatsapp' &&
    inbox.value.provider === 'unoapi'
);
const isOnChatwootCloud = useMapGetter('globalConfig/isOnChatwootCloud');
const { replaceInstallationName } = useBranding();

const isCaptainMessage = computed(() => {
  const senderType = props.sender?.type ?? props.senderType;
  return senderType === SENDER_TYPES.CAPTAIN_ASSISTANT;
});

/**
 * Computes the message variant based on props
 * @type {import('vue').ComputedRef<'user'|'agent'|'activity'|'private'|'bot'|'template'>}
 */
const variant = computed(() => {
  if (props.private) return MESSAGE_VARIANTS.PRIVATE;

  if (props.isEmailInbox) {
    const emailInboxTypes = [MESSAGE_TYPES.INCOMING, MESSAGE_TYPES.OUTGOING];
    if (emailInboxTypes.includes(props.messageType)) {
      return MESSAGE_VARIANTS.EMAIL;
    }
  }

  if (props.contentType === CONTENT_TYPES.INCOMING_EMAIL) {
    return MESSAGE_VARIANTS.EMAIL;
  }

  if (props.status === MESSAGE_STATUS.FAILED) return MESSAGE_VARIANTS.ERROR;
  if (props.contentAttributes?.isUnsupported)
    return MESSAGE_VARIANTS.UNSUPPORTED;

  if (props.contentAttributes?.externalEcho) {
    return MESSAGE_VARIANTS.AGENT;
  }

  const isBot =
    props.sender?.type === SENDER_TYPES.AGENT_BOT ||
    props.senderType === SENDER_TYPES.AGENT_BOT ||
    (!props.sender && !props.additionalAttributes?.senderName);
  if (isBot && props.messageType === MESSAGE_TYPES.OUTGOING) {
    return MESSAGE_VARIANTS.BOT;
  }

  const variants = {
    [MESSAGE_TYPES.INCOMING]: MESSAGE_VARIANTS.USER,
    [MESSAGE_TYPES.ACTIVITY]: MESSAGE_VARIANTS.ACTIVITY,
    [MESSAGE_TYPES.OUTGOING]: MESSAGE_VARIANTS.AGENT,
    [MESSAGE_TYPES.TEMPLATE]: MESSAGE_VARIANTS.TEMPLATE,
  };

  return variants[props.messageType] || MESSAGE_VARIANTS.USER;
});

const isBotOrAgentMessage = computed(() => {
  if (props.messageType === MESSAGE_TYPES.ACTIVITY) {
    return false;
  }
  // if an outgoing message is still processing, then it's definitely a
  // message sent by the current user
  if (
    props.status === MESSAGE_STATUS.PROGRESS &&
    props.messageType === MESSAGE_TYPES.OUTGOING
  ) {
    return true;
  }
  const senderId = props.senderId ?? props.sender?.id;
  const senderType = props.sender?.type ?? props.senderType;

  if (!senderType || !senderId) {
    return true;
  }

  if (
    [SENDER_TYPES.AGENT_BOT, SENDER_TYPES.CAPTAIN_ASSISTANT].includes(
      senderType
    )
  ) {
    return true;
  }

  return senderType.toLowerCase() === SENDER_TYPES.USER.toLowerCase();
});

const isCurrentUserSender = computed(() => {
  const senderId = props.senderId ?? props.sender?.id;
  if (!senderId || !props.currentUserId) return false;

  return Number(senderId) === Number(props.currentUserId);
});

/**
 * Computes the message orientation based on sender type and message type
 * @returns {import('vue').ComputedRef<'left'|'right'|'center'>} The computed orientation
 */
const orientation = computed(() => {
  if (props.messageType === MESSAGE_TYPES.ACTIVITY) {
    return ORIENTATION.CENTER;
  }

  if (isAnInternalChannel.value) {
    return isCurrentUserSender.value ? ORIENTATION.RIGHT : ORIENTATION.LEFT;
  }

  if (isBotOrAgentMessage.value) {
    return ORIENTATION.RIGHT;
  }

  return ORIENTATION.LEFT;
});

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const senderDisplayName = computed(() => {
  return (
    props.sender?.name ||
    props.additionalAttributes?.senderName ||
    props.sender?.phoneNumber ||
    props.sender?.phone_number ||
    ''
  );
});

const showIncomingGroupSender = computed(() => {
  return (
    props.isGroupConversation &&
    props.messageType === MESSAGE_TYPES.INCOMING &&
    !!senderDisplayName.value
  );
});

const hasAvatarColumnOnLeft = computed(() => {
  return (
    orientation.value === ORIENTATION.LEFT &&
    (isAnInternalChannel.value || showIncomingGroupSender.value)
  );
});

const gridClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: hasAvatarColumnOnLeft.value
      ? 'grid grid-cols-[24px_1fr]'
      : 'grid grid-cols-1fr',
    [ORIENTATION.RIGHT]: 'grid grid-cols-[1fr_24px]',
  };

  return map[orientation.value];
});

const gridTemplate = computed(() => {
  const map = hasAvatarColumnOnLeft.value
    ? {
        [ORIENTATION.LEFT]: `
      "avatar bubble"
      "spacer meta"
    `,
        [ORIENTATION.RIGHT]: `
      "bubble avatar"
      "meta spacer"
    `,
      }
    : {
        [ORIENTATION.LEFT]: `
      "bubble"
      "meta"
    `,
        [ORIENTATION.RIGHT]: `
      "bubble avatar"
      "meta spacer"
    `,
      };

  return map[orientation.value];
});

const shouldGroupWithNext = computed(() => {
  if (props.status === MESSAGE_STATUS.FAILED) return false;

  return props.groupWithNext;
});

const shouldShowAvatar = computed(() => {
  if (props.messageType === MESSAGE_TYPES.ACTIVITY) return false;

  if (hasAvatarColumnOnLeft.value) return true;
  if (orientation.value === ORIENTATION.LEFT) return false;

  return true;
});

const isMessageDeleted = computed(() => {
  return props.contentAttributes?.deleted;
});

const isDeletedContentPreserved = computed(() => {
  return (
    props.contentAttributes?.deleted_content_preserved ||
    props.contentAttributes?.deletedContentPreserved
  );
});

const shouldRenderDeletedPlaceholder = computed(() => {
  return isMessageDeleted.value && !isDeletedContentPreserved.value;
});

const viewOnceRevealed = ref(false);
const componentToRender = computed(() => {
  if (shouldRenderDeletedPlaceholder.value) {
    return TextBubble;
  }

  if (
    (props.contentAttributes.viewOnce || props.contentAttributes.view_once) &&
    !viewOnceRevealed.value
  )
    return ViewOnceBubble;

  if (props.isEmailInbox && !props.private) {
    const emailInboxTypes = [MESSAGE_TYPES.INCOMING, MESSAGE_TYPES.OUTGOING];
    if (emailInboxTypes.includes(props.messageType)) return EmailBubble;
  }

  if (props.contentAttributes?.unoapiMessageType) {
    return UnoapiCatalogBubble;
  }

  if (
    props.contentAttributes?.whatsappInteractive &&
    props.contentAttributes.whatsappInteractive.type !== 'carousel'
  ) {
    return UnoapiInteractiveBubble;
  }

  if (props.contentType === CONTENT_TYPES.INPUT_CSAT) {
    return CSATBubble;
  }

  if (
    [CONTENT_TYPES.INPUT_SELECT, CONTENT_TYPES.FORM].includes(props.contentType)
  ) {
    return FormBubble;
  }

  if (props.contentType === CONTENT_TYPES.VOICE_CALL) {
    return VoiceCallBubble;
  }

  if (props.contentType === CONTENT_TYPES.STICKER) {
    return StickerBubble;
  }

  if (props.contentType === CONTENT_TYPES.CARDS) {
    return CardsBubble;
  }

  if (props.contentType === CONTENT_TYPES.INCOMING_EMAIL) {
    return EmailBubble;
  }

  if (props.contentAttributes?.isUnsupported) {
    return UnsupportedBubble;
  }

  if (props.contentAttributes.type === 'dyte') {
    return DyteBubble;
  }

  const instagramSharedTypes = [
    ATTACHMENT_TYPES.STORY_MENTION,
    ATTACHMENT_TYPES.IG_STORY,
    ATTACHMENT_TYPES.IG_STORY_REPLY,
    ATTACHMENT_TYPES.IG_POST,
  ];
  if (instagramSharedTypes.includes(props.contentAttributes.imageType)) {
    return InstagramStoryBubble;
  }

  const contactAttachments = (props.attachments || []).filter(
    attachment => attachment.fileType === ATTACHMENT_TYPES.CONTACT
  );
  if (
    contactAttachments.length > 1 ||
    (!contactAttachments.length && props.contentAttributes?.contacts?.length)
  ) {
    return ContactsBubble;
  }

  if (Array.isArray(props.attachments) && props.attachments.length === 1) {
    const fileType = props.attachments[0].fileType;

    if (fileType === ATTACHMENT_TYPES.FALLBACK) return FallbackBubble;
    if (fileType === ATTACHMENT_TYPES.LOCATION) return LocationBubble;

    if (!props.content) {
      if (fileType === ATTACHMENT_TYPES.IMAGE) return ImageBubble;
      if (fileType === ATTACHMENT_TYPES.FILE) return FileBubble;
      if (fileType === ATTACHMENT_TYPES.AUDIO) return AudioBubble;
      if (fileType === ATTACHMENT_TYPES.VIDEO) return VideoBubble;
      if (fileType === ATTACHMENT_TYPES.IG_REEL) return VideoBubble;
      if (fileType === ATTACHMENT_TYPES.EMBED) return EmbedBubble;
    }
    // Attachment content is the name of the contact
    if (fileType === ATTACHMENT_TYPES.CONTACT) return ContactBubble;
  }

  return TextBubble;
});

const shouldShowContextMenu = computed(() => {
  return !props.contentAttributes?.isUnsupported;
});

const isBubble = computed(() => {
  return props.messageType !== MESSAGE_TYPES.ACTIVITY;
});

const payloadForContextMenu = computed(() => {
  return {
    id: props.id,
    content_attributes: props.contentAttributes,
    content: props.content,
    conversation_id: props.conversationId,
  };
});

const store = useStore();
const currentRole = computed(() => store.getters.getCurrentRole);
const accountId = computed(() => store.getters.getCurrentAccountId);
const isFeatureEnabledonAccount = computed(
  () => store.getters['accounts/isFeatureEnabledonAccount']
);

const contextMenuEnabledOptions = computed(() => {
  const hasText = !!props.content;
  const hasAttachments = !!(props.attachments && props.attachments.length > 0);

  const hideDeleteMessageForAgents =
    currentRole.value !== 'administrator' &&
    isFeatureEnabledonAccount.value(
      accountId.value,
      'hide_delete_message_for_agent'
    );

  const isOutgoing = props.messageType === MESSAGE_TYPES.OUTGOING;
  const isFailedOrProcessing =
    props.status === MESSAGE_STATUS.FAILED ||
    props.status === MESSAGE_STATUS.PROGRESS;

  return {
    copy: hasText,
    forward:
      (hasText || hasAttachments) &&
      !isFailedOrProcessing &&
      !isMessageDeleted.value,
    delete:
      !hideDeleteMessageForAgents &&
      (hasText || hasAttachments) &&
      !isFailedOrProcessing &&
      !isMessageDeleted.value,
    cannedResponse: isOutgoing && hasText && !isMessageDeleted.value,
    edit:
      isUnoapiInbox.value &&
      isOutgoing &&
      hasText &&
      !hasAttachments &&
      !!props.sourceId &&
      !props.private &&
      props.contentType === CONTENT_TYPES.TEXT &&
      !isFailedOrProcessing &&
      !isMessageDeleted.value,
    copyLink: !isFailedOrProcessing,
    translate: !isFailedOrProcessing && !isMessageDeleted.value && hasText,
    replyTo:
      !props.private &&
      props.inboxSupportsReplyTo.outgoing &&
      !isFailedOrProcessing,
    reaction:
      isAWhatsAppChannel.value &&
      !isATwilioWhatsAppChannel.value &&
      !!props.sourceId &&
      !isFailedOrProcessing &&
      !isMessageDeleted.value,
    report:
      isOnChatwootCloud.value &&
      isCaptainMessage.value &&
      !isMessageDeleted.value,
  };
});

const shouldRenderMessage = computed(() => {
  const hasAttachments = !!(props.attachments && props.attachments.length > 0);
  const isEmailContentType = props.contentType === CONTENT_TYPES.INCOMING_EMAIL;
  const isUnsupported = props.contentAttributes?.isUnsupported;
  const isStickerContentType = props.contentType === CONTENT_TYPES.STICKER;
  const isCardsContentType = props.contentType === CONTENT_TYPES.CARDS;
  const isAnIntegrationMessage =
    props.contentType === CONTENT_TYPES.INTEGRATIONS;
  const isFailedMessage = props.status === MESSAGE_STATUS.FAILED;
  const hasExternalError = !!props.contentAttributes?.externalError;

  return (
    hasAttachments ||
    props.contentAttributes?.contacts?.length ||
    props.content ||
    isEmailContentType ||
    isUnsupported ||
    isStickerContentType ||
    isCardsContentType ||
    isAnIntegrationMessage ||
    isFailedMessage ||
    hasExternalError ||
    isMessageDeleted.value
  );
});

const reactionEmoji = computed(() => {
  const reaction = props.contentAttributes?.reaction;
  if (!reaction) return '';
  if (typeof reaction === 'string') return reaction;
  return reaction.emoji || '';
});

const hasReaction = computed(() => !!reactionEmoji.value);

const messageContent = computed(() => {
  if (shouldRenderDeletedPlaceholder.value) {
    return t('GENERAL_SETTINGS.FORM.DELETED_MESSAGE_CONTENT.NOTICE');
  }

  return props.content;
});

const messageAttachments = computed(() => {
  if (shouldRenderDeletedPlaceholder.value) {
    return [];
  }

  return props.attachments;
});

const shouldShowDeletedMediaNotice = computed(() => {
  return (
    isDeletedContentPreserved.value && componentToRender.value !== TextBubble
  );
});

const longPressStartPoint = ref(null);
const longPressPoint = ref(null);
const longPressTarget = ref(null);

function openContextMenu(e) {
  const shouldSkipContextMenu =
    e.target?.classList.contains('skip-context-menu') ||
    ['a'].includes(e.target?.tagName.toLowerCase());
  if (shouldSkipContextMenu || getSelection().toString()) {
    return;
  }

  e.preventDefault();
  if (e.type === 'contextmenu') {
    useTrack(ACCOUNT_EVENTS.OPEN_MESSAGE_CONTEXT_MENU);
  }
  contextMenuPosition.value = {
    x: e.pageX || e.clientX,
    y: e.pageY || e.clientY,
  };
  showContextMenu.value = true;
}

function getTouchPoint(e) {
  const touch = e.touches?.[0] || e.changedTouches?.[0];
  if (!touch) return null;
  return {
    pageX: touch.pageX,
    pageY: touch.pageY,
    clientX: touch.clientX,
    clientY: touch.clientY,
  };
}

function createTouchContextEvent() {
  if (!longPressPoint.value || !longPressTarget.value) return null;
  return {
    type: 'contextmenu',
    target: longPressTarget.value,
    pageX: longPressPoint.value.pageX,
    pageY: longPressPoint.value.pageY,
    clientX: longPressPoint.value.clientX,
    clientY: longPressPoint.value.clientY,
    preventDefault: () => {},
  };
}

const { start: startLongPressTimer, stop: stopLongPressTimer } = useTimeoutFn(
  () => {
    const touchEvent = createTouchContextEvent();
    if (touchEvent) {
      openContextMenu(touchEvent);
    }
  },
  450,
  { immediate: false }
);

function cancelLongPress() {
  stopLongPressTimer();
  longPressStartPoint.value = null;
  longPressPoint.value = null;
  longPressTarget.value = null;
}

function handleTouchStart(e) {
  if (e.touches?.length !== 1) return;
  const point = getTouchPoint(e);
  if (!point) return;
  longPressStartPoint.value = { x: point.pageX, y: point.pageY };
  longPressPoint.value = point;
  longPressTarget.value = e.target;
  stopLongPressTimer();
  startLongPressTimer();
}

function handleTouchMove(e) {
  if (!longPressStartPoint.value) return;
  const point = getTouchPoint(e);
  if (!point) return;
  const deltaX = Math.abs(point.pageX - longPressStartPoint.value.x);
  const deltaY = Math.abs(point.pageY - longPressStartPoint.value.y);
  if (deltaX > 10 || deltaY > 10) {
    cancelLongPress();
    return;
  }
  longPressPoint.value = point;
}

function handleTouchEnd() {
  cancelLongPress();
}

function handleTouchCancel() {
  cancelLongPress();
}

function closeContextMenu() {
  showContextMenu.value = false;
  contextMenuPosition.value = { x: null, y: null };
}

function handleReplyTo() {
  const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
  const { conversationId, id: replyTo } = props;

  LocalStorage.updateJsonStore(replyStorageKey, conversationId, replyTo);
  emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, props);
}

function handleForwardMessages() {
  emitter.emit(BUS_EVENTS.FORWARD_MESSAGES, {
    conversationId: props.conversationId,
    messageId: props.id,
  });
}

const avatarInfo = computed(() => {
  if (props.contentAttributes?.externalEcho) {
    const { name, avatar_url, channel_type, medium } = inbox.value;
    const iconName = avatar_url
      ? null
      : getInboxIconByType(channel_type, medium);
    return {
      name: iconName ? '' : name || t('CONVERSATION.NATIVE_APP'),
      src: avatar_url || '',
      iconName,
    };
  }

  // If no sender, check for Slack (or other integration) sender info
  if (!props.sender) {
    const { senderName, senderAvatarUrl } = props.additionalAttributes || {};
    if (senderName) {
      return { name: senderName, src: senderAvatarUrl ?? '' };
    }
    return { name: t('CONVERSATION.BOT'), src: '' };
  }

  const { sender } = props;
  const { name, type, avatarUrl, thumbnail } = sender || {};

  // If sender type is agent bot, use avatarUrl
  if ([SENDER_TYPES.AGENT_BOT, SENDER_TYPES.CAPTAIN_ASSISTANT].includes(type)) {
    return {
      name: name ?? '',
      src: avatarUrl ?? '',
    };
  }

  // For all other senders, use thumbnail
  return {
    name: name ?? '',
    src: thumbnail ?? '',
  };
});

const avatarTooltip = computed(() => {
  if (props.contentAttributes?.externalEcho) {
    return replaceInstallationName(t('CONVERSATION.NATIVE_APP_ADVISORY'));
  }
  if (avatarInfo.value.name === '') return '';
  return `${t('CONVERSATION.SENT_BY')} ${avatarInfo.value.name}`;
});

const setupHighlightTimer = () => {
  const targetMessageId = route.query.messageId;

  if (targetMessageId === undefined || targetMessageId === null) {
    return;
  }

  if (String(targetMessageId) !== String(props.id)) {
    return;
  }

  showBackgroundHighlight.value = true;
  const HIGHLIGHT_TIMER = 1000;
  useTimeoutFn(() => {
    showBackgroundHighlight.value = false;
  }, HIGHLIGHT_TIMER);
};

onMounted(setupHighlightTimer);

provideMessageContext({
  ...toRefs(props),
  content: messageContent,
  attachments: messageAttachments,
  isPrivate: computed(() => props.private),
  variant,
  orientation,
  isBotOrAgentMessage,
  shouldGroupWithNext,
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div
    v-if="shouldRenderMessage"
    :id="`message${props.id}`"
    class="flex w-full message-bubble-container group/context-menu"
    :data-message-id="props.id"
    :class="[
      flexOrientationClass,
      {
        'group-with-next': shouldGroupWithNext,
        'bg-n-alpha-1': showBackgroundHighlight,
        'mb-6': hasReaction,
        'mb-2': !hasReaction,
      },
    ]"
  >
    <div v-if="variant === MESSAGE_VARIANTS.ACTIVITY">
      <ActivityBubble :content="content" />
    </div>
    <div
      v-else
      :class="[
        gridClass,
        {
          'gap-y-2': contentAttributes.externalError,
          'w-full': variant === MESSAGE_VARIANTS.EMAIL,
        },
      ]"
      class="gap-x-2"
      :style="{
        gridTemplateAreas: gridTemplate,
      }"
    >
      <div
        v-if="!shouldGroupWithNext && shouldShowAvatar"
        v-tooltip.left-end="avatarTooltip"
        class="[grid-area:avatar] flex items-end"
      >
        <Avatar v-bind="avatarInfo" :size="24" />
      </div>
      <div
        class="[grid-area:bubble] flex min-w-0"
        :class="{
          'ltr:ml-8 rtl:mr-8 justify-end': orientation === ORIENTATION.RIGHT,
          'ltr:mr-8 rtl:ml-8':
            orientation === ORIENTATION.LEFT && !hasAvatarColumnOnLeft,
        }"
        @contextmenu="openContextMenu($event)"
        @touchstart.passive="handleTouchStart"
        @touchmove.passive="handleTouchMove"
        @touchend.passive="handleTouchEnd"
        @touchcancel.passive="handleTouchCancel"
      >
        <div
          v-if="props.isForwardSelectionActive"
          class="flex items-start pt-1 ltr:mr-2 rtl:ml-2"
        >
          <input
            type="checkbox"
            class="mt-1 size-4 rounded border border-n-weak"
            :checked="props.isForwardSelected"
            @change="emit('toggle-forward-selection', props.id)"
          />
        </div>
        <div
          class="relative inline-flex flex-col max-w-full"
          :class="{
            'w-full': variant === MESSAGE_VARIANTS.EMAIL,
          }"
        >
          <span
            v-if="showIncomingGroupSender"
            class="mb-1 text-xs font-medium leading-4 text-n-slate-11"
          >
            {{ t('CONVERSATION.SENT_BY') }} {{ senderDisplayName }}
          </span>
          <Component
            :is="componentToRender"
            @reveal="viewOnceRevealed = true"
          />
          <UnoapiWarning :content-attributes="contentAttributes" />
          <span
            v-if="shouldShowDeletedMediaNotice"
            class="mt-1 px-3 text-xs font-medium text-n-slate-11"
          >
            {{ t('GENERAL_SETTINGS.FORM.DELETED_MESSAGE_CONTENT.NOTICE') }}
          </span>
          <div
            v-if="hasReaction"
            class="absolute -bottom-3 left-3 inline-flex items-center rounded-full border border-n-weak bg-n-background px-2 py-0.5 text-xs text-n-slate-12 shadow-sm"
          >
            <span class="leading-none">{{ reactionEmoji }}</span>
          </div>
        </div>
      </div>
      <MessageError
        v-if="contentAttributes.externalError"
        class="[grid-area:meta]"
        :class="flexOrientationClass"
        :error="contentAttributes.externalError"
        @retry="emit('retry')"
      />
    </div>
    <div v-if="shouldShowContextMenu" class="context-menu-wrap">
      <ContextMenu
        v-if="isBubble"
        :context-menu-position="contextMenuPosition"
        :is-open="showContextMenu"
        :enabled-options="contextMenuEnabledOptions"
        :message="payloadForContextMenu"
        @open="openContextMenu"
        @close="closeContextMenu"
        @reply-to="handleReplyTo"
        @forward="handleForwardMessages"
      />
    </div>
  </div>
</template>

<style lang="scss">
.group-with-next + .message-bubble-container {
  .left-bubble {
    @apply ltr:rounded-tl-sm rtl:rounded-tr-sm;
  }

  .right-bubble {
    @apply ltr:rounded-tr-sm rtl:rounded-tl-sm;
  }
}
</style>
